import React, { useEffect, useState, useRef } from 'react';
import PageHeader from '../../components/ui/PageHeader';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import ActionToolbar from '../../components/ui/ActionToolbar';
import { CajaService } from '../../services/CajaService';
import { useAuth } from '../../context/AuthContext';

const Cajas = () => {
    const { user } = useAuth();
    const toast = useRef(null);
    const [cajas, setCajas] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Estados de dialogo
    const [cajaDialog, setCajaDialog] = useState(false);
    const [cerrarDialog, setCerrarDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    
    // Entidad
    const emptyCaja = {
        montoInicial: 0,
        observacionesApertura: '',
        idEmpresa: user?.idEmpresa || 1,
        idSucursal: user?.idSucursal || 1
    };
    const [caja, setCaja] = useState(emptyCaja);
    
    // Entidad Cierre
    const emptyCierre = {
        montoFinal: 0,
        observacionesCierre: ''
    };
    const [cierre, setCierre] = useState(emptyCierre);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await CajaService.getAll();
            setCajas(data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las cajas', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const openNew = () => {
        setCaja(emptyCaja);
        setSubmitted(false);
        setCajaDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setCajaDialog(false);
        setCerrarDialog(false);
    };

    const saveCaja = async () => {
        setSubmitted(true);
        if (caja.montoInicial >= 0) {
            try {
                await CajaService.create(caja);
                toast.current.show({ severity: 'success', summary: 'Caja Abierta', detail: 'Se ha iniciado la caja exitosamente.', life: 3000 });
                setCajaDialog(false);
                loadData();
            } catch (error) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Ocurrió un error al abrir la caja.', life: 3000 });
            }
        }
    };

    const confirmCerrarCaja = (cajaRow) => {
        setCaja(cajaRow);
        setCierre(emptyCierre);
        setCerrarDialog(true);
    };

    const cerrarCajaSubmit = async () => {
        try {
            await CajaService.cerrarCaja(caja.id, cierre);
            toast.current.show({ severity: 'success', summary: 'Caja Cerrada', detail: 'Se ha cerrado la caja exitosamente.', life: 3000 });
            setCerrarDialog(false);
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cerrar la caja.', life: 3000 });
        }
    };

    const estadoBody = (row) => {
        const estado = (row.estado || '').toUpperCase();
        const severity = estado === 'ABIERTA' ? 'success' : 'danger';
        return <Tag value={estado} severity={severity} />;
    };

    const dineroBody = (monto) => `S/ ${Number(monto || 0).toFixed(2)}`;

    const actionBody = (rowData) => {
        if (rowData.estado === 'abierta') {
            return <Button label="Cerrar Caja" icon="pi pi-lock"  size="small" onClick={() => confirmCerrarCaja(rowData)} />;
        }
        return <Button label="Ver Detalles" icon="pi pi-eye" outlined size="small" disabled />;
    };

    return (
        <div className="grid">
            <div className="col-12">
                <PageHeader 
                    title="Gestión de Cajas" 
                    description="Supervise las cajas registradoras, sus arqueos y cierres por sucursal." 
                    icon="pi pi-wallet" 
                />
                <div className="card shadow-2 border-round-xl">
                    <Toast ref={toast} />
                    
                    <ActionToolbar
                        onNew={openNew}
                        newLabel="Abrir Nueva Caja"
                        onSearch={(val) => setGlobalFilter(val)}
                        searchValue={globalFilter}
                        searchPlaceholder="Buscar cajas..."
                    />

                    <DataTable value={cajas} loading={loading} paginator rows={10} globalFilter={globalFilter} emptyMessage="No hay cajas registradas.">
                        <Column field="fechaApertura" header="Apertura" body={(row) => new Date(row.fechaApertura).toLocaleString()} sortable />
                        <Column field="fechaCierre" header="Cierre" body={(row) => row.fechaCierre ? new Date(row.fechaCierre).toLocaleString() : '-'} sortable />
                        <Column field="estado" header="Estado" body={estadoBody} sortable />
                        <Column field="montoInicial" header="Monto Ini." body={(row) => dineroBody(row.montoInicial)} sortable />
                        <Column field="montoFinal" header="Monto Fin." body={(row) => dineroBody(row.montoFinal)} sortable />
                        <Column body={actionBody} headerStyle={{ width: '12rem' }} />
                    </DataTable>

                    {/* Dialog Apertura */}
                    <Dialog visible={cajaDialog} style={{ width: '450px' }} header="Apertura de Caja" modal footer={(
                        <>
                            <Button label="Cancelar" icon="pi pi-times" outlined onClick={hideDialog} />
                            <Button label="Abrir" icon="pi pi-check" onClick={saveCaja} />
                        </>
                    )} onHide={hideDialog}>
                        <div className="field mb-4">
                            <label htmlFor="montoInicial" className="font-bold">Monto Inicial (Sencillo) S/</label>
                            <InputNumber id="montoInicial" value={caja.montoInicial} onValueChange={(e) => setCaja({...caja, montoInicial: e.value})} mode="currency" currency="PEN" locale="es-PE" className="w-full" autoFocus />
                            {submitted && caja.montoInicial == null && <small className="p-error">El monto es requerido.</small>}
                        </div>
                        <div className="field">
                            <label htmlFor="observacionesApertura" className="font-bold">Observaciones</label>
                            <InputText id="observacionesApertura" value={caja.observacionesApertura} onChange={(e) => setCaja({...caja, observacionesApertura: e.target.value})} className="w-full" />
                        </div>
                    </Dialog>

                    {/* Dialog Cierre */}
                    <Dialog visible={cerrarDialog} style={{ width: '450px' }} header="Cierre de Caja" modal footer={(
                        <>
                            <Button label="Cancelar" icon="pi pi-times" outlined onClick={hideDialog} />
                            <Button label="Ejecutar Cierre" icon="pi pi-lock"  onClick={cerrarCajaSubmit} />
                        </>
                    )} onHide={hideDialog}>
                        <p className="mb-4 text-600">Por favor, cuente el dinero en caja e ingrese el monto final para realizar el cuadre.</p>
                        <div className="field mb-4">
                            <label htmlFor="montoFinal" className="font-bold">Monto Físico (Final) S/</label>
                            <InputNumber id="montoFinal" value={cierre.montoFinal} onValueChange={(e) => setCierre({...cierre, montoFinal: e.value})} mode="currency" currency="PEN" locale="es-PE" className="w-full" autoFocus />
                        </div>
                        <div className="field">
                            <label htmlFor="observacionesCierre" className="font-bold">Observaciones/Justificación</label>
                            <InputText id="observacionesCierre" value={cierre.observacionesCierre} onChange={(e) => setCierre({...cierre, observacionesCierre: e.target.value})} className="w-full" />
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Cajas;
